/**
 * BLOM Cybersource Unified Checkout (browser SDK helpers).
 */

declare global {
  interface Window {
    VAS?: {
      UnifiedCheckout: (jwt: string) => Promise<{
        createCheckout: (opts?: {autoProcessing?: boolean}) => Promise<{
          mount: (target?: string | Record<string, string>) => Promise<string>
          complete?: (token: string) => Promise<unknown>
          destroy?: () => void
          unmount?: () => void
        }>
        destroy?: () => void
      }>
    }
  }
}

function loadScript(src: string): Promise<void> {
  return new Promise((resolve, reject) => {
    const existing = document.querySelector(`script[src="${src}"]`)
    if (existing) {
      resolve()
      return
    }

    const script = document.createElement('script')
    script.src = src
    script.async = true
    script.onload = () => resolve()
    script.onerror = () => reject(new Error('Failed to load UnifiedCheckout.js'))
    document.head.appendChild(script)
  })
}

export async function loadUnifiedCheckoutScript(clientLibraryUrl: string): Promise<void> {
  if (!clientLibraryUrl.startsWith('https://')) {
    throw new Error('Invalid Unified Checkout library URL')
  }
  await loadScript(clientLibraryUrl)
}

export async function mountBlomCheckout(
  captureContextJwt: string,
  mountSelector = '#blom-uc-mount'
): Promise<string> {
  if (!window.VAS?.UnifiedCheckout) {
    throw new Error('UnifiedCheckout SDK not loaded')
  }

  const client = await window.VAS.UnifiedCheckout(captureContextJwt)
  const checkout = await client.createCheckout({autoProcessing: false})
  const transientToken = await checkout.mount(mountSelector)

  return String(transientToken)
}

export function ensureBlomMountElement(id = 'blom-uc-mount'): HTMLElement {
  let el = document.getElementById(id)
  if (el) {
    return el
  }

  el = document.createElement('div')
  el.id = id
  el.style.minHeight = '320px'
  el.style.width = '100%'
  document.body.appendChild(el)
  return el
}
